import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { startSmoothScroll, stopSmoothScroll } from '@/lib/smooth-scroll'
import { Logo } from '@/components/generals/logo'
import { buttonClasses } from '@/components/generals/button-link'
import { primaryNav, routes } from '@/constants/site'

const isActivePath = (pathname: string, href: string): boolean => {
    return pathname === href || pathname.startsWith(`${href}/`)
}

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [collapsed, setCollapsed] = useState(false)
    const [open, setOpen] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const update = () => setCollapsed(window.scrollY > 40)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        const wide = window.matchMedia('(min-width: 992px)')
        const handleChange = () => {
            if (wide.matches) setOpen(false)
        }

        wide.addEventListener('change', handleChange)

        return () => wide.removeEventListener('change', handleChange)
    }, [])

    useEffect(() => {
        if (!open) return

        const page = document.getElementById('page')
        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        page?.setAttribute('inert', '')
        stopSmoothScroll()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            setOpen(false)
            toggleRef.current?.focus()
        }

        window.addEventListener('keydown', handleKey)

        return () => {
            document.body.style.overflow = previousOverflow
            page?.removeAttribute('inert')
            startSmoothScroll()
            window.removeEventListener('keydown', handleKey)
        }
    }, [open])

    return (
        <>
            <a
                href='#main'
                className='sr-only z-[70] rounded-full bg-ink px-4 py-2 text-[13px] text-paper focus:not-sr-only focus:fixed focus:left-4 focus:top-4'
            >
                Skip to content
            </a>

            <header className='section-x fixed inset-x-0 top-0 z-50 flex items-center justify-between py-[14px]'>
                {/* Progressive veil: the blur and cream dissolve over an apron below the bar */}
                <span
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute inset-x-0 -bottom-9 top-0 -z-10 transition-opacity duration-300',
                        'bg-[linear-gradient(180deg,rgb(237_232_225/0.78)_0%,rgb(237_232_225/0.55)_55%,rgb(237_232_225/0)_100%)] backdrop-blur-[12px]',
                        '[mask-image:linear-gradient(180deg,#000_0%,#000_50%,transparent_100%)] [-webkit-mask-image:linear-gradient(180deg,#000_0%,#000_50%,transparent_100%)]',
                        open && 'opacity-0'
                    )}
                />

                <Link href={routes.home} aria-label='Kerning, home' className='relative z-[96] inline-flex items-center rounded-sm'>
                    <Logo collapsed={collapsed && !open} />
                </Link>

                <nav aria-label='Primary' className='absolute left-1/2 hidden -translate-x-1/2 min-[992px]:block'>
                    <ul className='flex items-center gap-7 text-[14px] text-ink/55'>
                        {primaryNav.map(item => {
                            const active = isActivePath(router.pathname, item.href)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn('transition-colors duration-200 hover:text-ink', active && 'font-medium text-ink')}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='relative z-[96] flex items-center gap-2.5'>
                    <Link href={routes.getStarted} className={buttonClasses()}>
                        Get started
                    </Link>

                    <Link href={routes.logIn} className={buttonClasses({ variant: 'ghost', className: 'hidden min-[992px]:inline-flex' })}>
                        Log in
                    </Link>

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        onClick={() => setOpen(value => !value)}
                        className='relative size-9 rounded-full border border-ink/25 bg-paper/55 backdrop-blur-[10px] min-[992px]:hidden'
                    >
                        <i className={cn('absolute left-1/2 top-1/2 h-[1.5px] w-3.5 bg-ink transition-transform duration-[250ms]', open ? '-translate-x-1/2 -translate-y-1/2 rotate-45' : '-translate-x-1/2 -translate-y-[calc(50%_+_2.5px)]')} />
                        <i className={cn('absolute left-1/2 top-1/2 h-[1.5px] w-3.5 bg-ink transition-transform duration-[250ms]', open ? '-translate-x-1/2 -translate-y-1/2 -rotate-45' : '-translate-x-1/2 -translate-y-[calc(50%_-_2.5px)]')} />
                    </button>
                </div>
            </header>

            {/* The menu grows out of the burger as an expanding circle */}
            <div
                id='mobile-menu'
                aria-hidden={!open}
                inert={!open}
                className={cn(
                    'fixed inset-0 z-40 flex flex-col items-center justify-center gap-10 bg-[rgb(237_232_225/0.97)] min-[992px]:hidden',
                    open
                        ? 'visible opacity-100 [clip-path:circle(142%_at_calc(100%_-_34px)_36px)] [transition:clip-path_350ms_cubic-bezier(0.34,1.25,0.64,1),opacity_150ms_ease]'
                        : 'invisible opacity-0 [clip-path:circle(18px_at_calc(100%_-_34px)_36px)] [transition:clip-path_250ms_cubic-bezier(0.22,1,0.36,1),opacity_200ms_ease,visibility_250ms_ease]'
                )}
            >
                <nav aria-label='Mobile' className='flex flex-col items-center gap-[22px]'>
                    {primaryNav.map((item, index) => {
                        const active = isActivePath(router.pathname, item.href)

                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'font-serif text-[clamp(30px,6vw,38px)] leading-[1.15] tracking-[-0.03em] text-ink transition-[opacity,transform,filter] duration-300',
                                    open ? 'translate-y-0 opacity-100 blur-0' : 'translate-y-2 opacity-0 blur-[2px]'
                                )}
                                style={{ transitionDelay: open ? `${50 + index * 50}ms` : '0ms' }}
                            >
                                {item.label}
                            </Link>
                        )
                    })}
                </nav>

                <Link
                    href={routes.getStarted}
                    className={buttonClasses({ size: 'lg', className: cn('transition-opacity duration-300', open ? 'opacity-100 delay-200' : 'opacity-0') })}
                >
                    Get started
                </Link>

                <Link
                    href={routes.logIn}
                    className={cn('-mt-6 text-[14px] font-medium text-ink transition-opacity duration-300', open ? 'opacity-80 delay-[250ms]' : 'opacity-0')}
                >
                    Log in
                </Link>
            </div>
        </>
    )
}
